import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ContentCopy as ContentCopyIcon,
  InsertDriveFileOutlined as FileIcon,
  DeleteOutline as DeleteIcon,
  DownloadOutlined as DownloadIcon,
  UploadFileOutlined as UploadFileIcon,
} from "@mui/icons-material";
import file from "../../../components/public/Template_productos.xlsx";
import productsServices from "../../../services/productsService";

const LoadMassive = ({ open, onClose, handleCreateProducto }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alertObj, setAlertObj] = useState({
    severity: "success",
    message: "",
    details: null,
  });
  const fileInputRef = useRef(null);
  const user_id = sessionStorage.getItem("User");

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const validateFile = (file) => {
    const allowedTypes = [".csv", ".xlsx"];
    const maxSize = 5 * 1024 * 1024;

    const fileExtension = "." + file.name.split(".").pop();

    if (!allowedTypes.includes(fileExtension)) {
      setUploadError("Solo se permiten archivos CSV y Excel (.csv, .xlsx)");
      return false;
    }

    if (file.size > maxSize) {
      setUploadError("El archivo no debe superar 5 MB");
      return false;
    }

    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files[0];

    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatUploadMessage = (responseData) => {
    const {
      row_succes = 0,
      row_error = 0,
      duplicate_products = [],
    } = responseData;

    let message = `Archivo subido exitosamente:`;

    return {
      message,
      details: {
        row_succes,
        row_error,
        duplicate_products,
      },
    };
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Por favor, selecciona un archivo");
      return;
    }

    try {
      setUploadProgress(0);
      const totalSteps = 100;

      const uploadInterval = setInterval(() => {
        setUploadProgress((prevProgress) => {
          if (prevProgress >= totalSteps) {
            clearInterval(uploadInterval);
            setSnackbarOpen(true);
            handleClearFile();
            onClose();
            return totalSteps;
          }
          return prevProgress + 10;
        });
      }, 200);

      const base64File = await convertToBase64(selectedFile);

      const payload = {
        user_id: parseInt(user_id, 10),
        file: base64File,
        file_name: selectedFile.name,
      };

      let response = await productsServices.postProductMassive(payload);

      if (response?.data) {
        const formattedResponse = formatUploadMessage(response.data);

        setAlertObj({
          severity: response.data.row_error > 0 ? "warning" : "success",
          message: formattedResponse.message,
          details: formattedResponse.details,
        });
        setTimeout(() => {
          handleCreateProducto();
        }, 1500);
      }
    } catch (error) {
      setAlertObj({
        severity: "error",
        message: "Error al subir el archivo.",
        details: null,
      });
      setSnackbarOpen(true);
      handleClearFile();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setUploadError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderAlertContent = () => {
    if (!alertObj.details) {
      return alertObj.message;
    }

    const { row_succes, row_error, duplicate_products } = alertObj.details;

    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {alertObj.message}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {row_succes > 0 && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`${row_succes} exitosos`}
              color="success"
              size="small"
            />
          )}

          {row_error > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${row_error} errores`}
              color="error"
              size="small"
            />
          )}

          {duplicate_products.length > 0 && (
            <Chip
              icon={<ContentCopyIcon />}
              label={`${duplicate_products.length} duplicados`}
              color="warning"
              size="small"
            />
          )}
        </Box>

        {duplicate_products.length > 0 && (
          <Box sx={{ mt: 1, width: "100%" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
              }}
            >
              Productos duplicados:
            </Typography>
            <Box
              sx={{
                maxHeight: 60,
                overflowY: "auto",
                width: "100%",
                textAlign: "left",
              }}
            >
              {duplicate_products.map((product, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  display="block"
                  sx={{
                    pl: 1,
                    width: "100%",
                    textAlign: "left",
                    boxSizing: "border-box",
                  }}
                >
                  •{" "}
                  {typeof product === "string"
                    ? product
                    : JSON.stringify(product)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const isUploading = uploadProgress > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={isUploading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 0,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Carga masiva de productos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Importa productos desde un archivo CSV o Excel
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isUploading}
            size="small"
            sx={{
              color: "text.secondary",
              alignSelf: "flex-start",
              mt: -0.5,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {/* Drop zone */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
            sx={{
              border: "2px dashed",
              borderColor: uploadError
                ? "error.main"
                : isDragActive
                  ? "primary.main"
                  : selectedFile
                    ? "success.main"
                    : "grey.300",
              borderRadius: 2,
              py: selectedFile ? 2.5 : 5,
              px: 3,
              textAlign: "center",
              bgcolor: isDragActive
                ? "action.hover"
                : selectedFile
                  ? "success.50"
                  : "grey.50",
              transition: "all 0.2s ease",
              cursor: selectedFile ? "default" : "pointer",
              "&:hover": !selectedFile
                ? {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  }
                : {},
            }}
          >
            <input
              type="file"
              accept=".csv, .xlsx"
              onChange={handleFileChange}
              style={{ display: "none" }}
              ref={fileInputRef}
            />

            {!selectedFile ? (
              <>
                <CloudUploadIcon
                  sx={{
                    fontSize: 44,
                    color: isDragActive ? "primary.main" : "text.disabled",
                    mb: 1.5,
                  }}
                />
                <Typography
                  variant="body1"
                  fontWeight={500}
                  color="text.primary"
                >
                  Arrastra tu archivo aqui
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  o{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    color="primary.main"
                    fontWeight={600}
                    sx={{ cursor: "pointer" }}
                  >
                    hace clic para seleccionar
                  </Typography>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: "block", mt: 1.5 }}
                >
                  CSV o Excel - Max. 5 MB
                </Typography>
              </>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ px: 1 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    borderRadius: 1.5,
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileIcon sx={{ color: "white", fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFile();
                  }}
                  disabled={isUploading}
                  sx={{ color: "text.secondary" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>

          {/* Error message */}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 1.5 }} variant="outlined">
              {uploadError}
            </Alert>
          )}

          {/* Progress bar */}
          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Subiendo archivo...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ borderRadius: 1, height: 6 }}
              />
            </Box>
          )}

          {/* Template download */}
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "grey.50",
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Plantilla de ejemplo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Descarga la plantilla con el formato correcto
              </Typography>
            </Box>
            <Button
              component="a"
              href={file}
              download
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Descargar
            </Button>
          </Box>
        </DialogContent>

        {/* Footer actions */}
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={isUploading}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            startIcon={<UploadFileIcon />}
            sx={{
              textTransform: "none",
              px: 3,
              borderRadius: 1.5,
              fontWeight: 600,
            }}
          >
            {isUploading ? "Subiendo..." : "Subir archivo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          maxWidth: "500px",
          zIndex: 9999,
          mt: 8,
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={alertObj.severity}
          sx={{ width: "100%" }}
        >
          {renderAlertContent()}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoadMassive;
